import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Linkedin } from "lucide-react";
import { api } from "@/api";

const DEFAULT_BG = "linear-gradient(135deg, #8FA678 0%, #3F5A2E 100%)";

function initialsFrom(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "K";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function SceneLeadership() {
  const [leaders, setLeaders] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/leaders");
        if (!cancelled) setLeaders(data);
      } catch {
        if (!cancelled) setLeaders([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section
      id="leadership"
      data-testid="scene-leadership"
      className="relative w-full bg-beige overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -left-20 w-[400px] h-[400px] rounded-full bg-[var(--k-olive-soft)] opacity-40 blur-[100px]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[var(--k-sand)] opacity-50 blur-[120px]" />
      </div>

      <div className="section relative z-10">
        <div className="container-k">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.9 }}
            className="text-center max-w-3xl mx-auto mb-20"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-12 h-px bg-[var(--k-olive)]" />
              <span className="font-mono-label text-[var(--k-gold)]">Chapter 05</span>
              <div className="w-12 h-px bg-[var(--k-olive)]" />
            </div>
            <h2
              className="font-serif-display text-5xl md:text-7xl text-[var(--k-text)] leading-[1]"
              data-testid="leadership-headline"
            >
              Leadership <em className="green-gradient-text not-italic">Team</em>
            </h2>
            <p className="font-serif-display italic text-2xl text-[var(--k-text-2)] mt-6"
               data-testid="leadership-subheadline"
            >
              Driving growth through vision, systems & execution.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-7">
            {leaders.map((l, i) => {
              const bg = l.bg || DEFAULT_BG;
              const initials = l.initials || initialsFrom(l.name);
              const AvatarWrapperTag = l.linkedin_url ? "a" : "div";
              const socialProps = l.linkedin_url
                ? { href: l.linkedin_url, target: "_blank", rel: "noopener noreferrer" }
                : {};
              return (
                <motion.article
                  key={l.id || l.name}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, delay: i * 0.12 }}
                  className="organic-card p-8 group text-center"
                  data-testid={`leader-card-${i}`}
                >
                  {/* Avatar */}
                  <div className="relative w-32 h-32 mx-auto mb-6">
                    <div className="absolute inset-0 rounded-full opacity-30 blur-xl transition-opacity duration-500 group-hover:opacity-60"
                         style={{ background: bg }}
                    />
                    <div
                      className="relative w-32 h-32 rounded-full overflow-hidden flex items-center justify-center border-4 border-[var(--k-card)] shadow-lg transition-transform duration-500 group-hover:scale-105"
                      style={{ background: bg }}
                    >
                      {l.image_url ? (
                        <img
                          src={l.image_url}
                          alt={l.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="font-serif-display text-4xl text-[var(--k-card)] tracking-wide drop-shadow-sm">
                          {initials}
                        </span>
                      )}
                    </div>
                    <AvatarWrapperTag
                      {...socialProps}
                      className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[var(--k-card)] border border-[var(--k-line)] flex items-center justify-center shadow-md cursor-pointer hover:bg-[var(--k-green)] hover:border-[var(--k-green)] transition-colors group/social"
                      data-testid={`leader-social-${i}`}
                      aria-label={l.linkedin_url ? `${l.name} on LinkedIn` : undefined}
                    >
                      <Linkedin className="w-3.5 h-3.5 text-[var(--k-green)] group-hover/social:text-[var(--k-card)] transition-colors" strokeWidth={1.6} />
                    </AvatarWrapperTag>
                  </div>

                  <h3 className="font-serif-display text-2xl text-[var(--k-text)] mb-1"
                      data-testid={`leader-name-${i}`}>
                    {l.name}
                  </h3>
                  <div className="font-mono-label text-[var(--k-green)] mb-4" data-testid={`leader-designation-${i}`}>
                    {l.designation}
                  </div>
                  <div className="hairline-green w-12 mx-auto mb-4 opacity-60" />
                  <p className="text-[var(--k-text-2)] text-sm leading-relaxed">
                    {l.blurb}
                  </p>
                </motion.article>
              );
            })}
          </div>

          {/* Tagline reveal */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1.2 }}
            className="mt-32 text-center"
          >
            <div className="hairline-green w-40 mx-auto mb-10" />
            <h3
              className="font-serif-display text-4xl md:text-6xl lg:text-7xl text-[var(--k-text)] leading-[1.05] max-w-5xl mx-auto"
              data-testid="tagline-reveal"
            >
              <span className="green-gradient-text">KOSTRA</span> — Building India's <br />
              Next Generation <em className="italic text-[var(--k-text-2)]">Cloud Kitchen Network.</em>
            </h3>
            <div className="hairline-green w-40 mx-auto mt-10" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
